import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { catchError } from 'rxjs/operators';

import { firstValueFrom, of } from 'rxjs';

import { Roles } from '../schemas';
import { Lambda } from './lambda';
import { Messenger } from './messenger';
import { Token } from './token';

type Role = keyof typeof Roles;

interface LinkUser {
  FullName: string;
  Email: string;
  Role: Role;
  at: string;
}

const SUPERADMINS = ['sergey.motornyuk@linkdigital.com.au'];

@Injectable({ providedIn: 'root' })
export class Auth {
  role = signal<Role>('ROLE_USER');
  isAdmin = computed(() => this.role().includes('ROLE_ADMIN'));
  isAdminOrEditor = computed(() => this.isAdmin() || this.role().includes('ROLE_EDITOR'));
  isSuperAdmin = computed(() => SUPERADMINS.includes(this.email()));

  name = signal('');
  email = signal('');
  isLoggedIn = computed(() => !!this.email());

  public redirectUrl = '/app/dashboard';

  token = inject(Token);
  lambda = inject(Lambda);

  http = inject(HttpClient);
  router = inject(Router);
  messenger = inject(Messenger);

  showSignIn = signal(false);

  constructor() {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: '836850100882-rmkcr41lqd31d0eck3jas8fqqo6q9e1i.apps.googleusercontent.com',
        callback: (response) => this.loginCallback(response),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
    }

    const token = this.token.social();
    if (!token) {
      this.showSignIn.set(true);
      return;
    }

    this.messenger.info('Found existing access token. Try to identify user with it..');
    this.revealUser(token);
  }

  private async revealUser(token: string) {
    const user = await firstValueFrom(
      this.http
        .post<LinkUser>(this.lambda.getSigninCallbackUrl(), {
          token: token,
        })
        .pipe(
          catchError((resp: HttpErrorResponse) => {
            console.warn(resp);
            const err = resp.error;

            if (err && err.error) {
              this.messenger.error(err.error);
            } else {
              this.messenger.error('Cannot authorize the user.');
            }
            return of(undefined);
          }),
        ),
    );
    if (!user) {
      this.showSignIn.set(true);
      return;
    }
    this.token.auth.set(user.at);
    this.setPerson(user.FullName, user.Email, user.Role);

    this.messenger.info(`Hello, ${this.name().trim()}! Application is loading..`);
    this.router.navigateByUrl(this.redirectUrl);
  }

  private loginCallback(response: google.accounts.id.CredentialResponse) {
    this.token.social.set(response.credential);
    return this.revealUser(response.credential);
  }

  logout() {
    this.setPerson(); // Clear user's name, email, and role
    this.token.auth.set(''); // Clear the backend access token
    this.token.social.set(''); // Clear the social ID token
    this.showSignIn.set(true);
  }

  setPerson(name?: string, email?: string, role?: Role) {
    this.name.set(name || '');
    this.email.set(email || '');
    this.role.set(role || 'ROLE_USER');
  }
}
